import {isSameDay} from 'date-fns';

import {isNil} from 'ramda';

import type {RentalCalendarScale} from '../types/RentalCalendarScale';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';

export interface RentalCalendarDayGroup {
  /** Index into `scale.columns` — the day this group is drawn over */
  columnIndex: number;
  day: Date;
  events: RentalResourceCalendarEvent[];
}

export interface AggregatedRowEvents {
  /** Events drawn as ordinary time-positioned bars */
  bars: RentalResourceCalendarEvent[];
  /** Days holding several sub-day rentals, drawn as one count chip each */
  groups: RentalCalendarDayGroup[];
}

/**
 * A month column is one day wide, so a rental shorter than a day renders as a few pixels and
 * several of them land on top of each other. Those are collected per day into one chip. A chip is
 * drawn over exactly one column, so only rentals that start and end on the same calendar day can
 * join one — a short rental crossing midnight stays a bar, kept visible by the minimum bar width.
 * Day view (hour columns) never aggregates.
 */
export function aggregateSameDayEvents(
  events: RentalResourceCalendarEvent[],
  scale: RentalCalendarScale
): AggregatedRowEvents {
  if (scale.view !== 'month') {
    return {bars: events, groups: []};
  }

  const eventsByColumn = new Map<number, RentalResourceCalendarEvent[]>();
  const bars: RentalResourceCalendarEvent[] = [];

  events.forEach((event) => {
    const columnIndex = scale.columns.findIndex((column) => isSameDay(column, event.start));
    const isWithinOneDay = columnIndex >= 0 && isSameDay(event.start, event.end);

    if (!isWithinOneDay || event.type === 'block') {
      bars.push(event);
      return;
    }

    const columnEvents = eventsByColumn.get(columnIndex);

    if (columnEvents) {
      columnEvents.push(event);
    } else {
      eventsByColumn.set(columnIndex, [event]);
    }
  });

  const groups: RentalCalendarDayGroup[] = [];

  eventsByColumn.forEach((columnEvents, columnIndex) => {
    const day = scale.columns.at(columnIndex);

    // A lone short rental stays a bar — the minimum bar width keeps it visible
    if (columnEvents.length < MINIMUM_EVENTS_TO_AGGREGATE || isNil(day)) {
      bars.push(...columnEvents);
      return;
    }

    groups.push({columnIndex, day, events: columnEvents});
  });

  return {bars, groups};
}

/** Below this a day is drawn as bars; from it the day collapses into one chip */
const MINIMUM_EVENTS_TO_AGGREGATE = 2;
